<template>
  <div class="locations-container">
    <div class="title_line flex justify-between">
      <div class="flex w-1/3 justify-between items-center">
        <h1 class="text-left">Locations</h1>
      </div>

      <div class="flex w-2/3 justify-end button_group">
        <button @click="openAddLocation" class="flex items-center in_row_button square_button square_green_button">
          <svg width="18" height="18" viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M9.5 1V16.9996" stroke="#1FBC2F" stroke-width="2" stroke-linecap="round" />
            <path d="M17.5 9L1.50038 9" stroke="#1FBC2F" stroke-width="2" stroke-linecap="round" />
          </svg>
        </button>
        <button @click="openDeleteModal" :disabled="!show_buttons"
          class="flex items-center in_row_button square_button square_red_button">
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M8 4C8 2.89543 8.89543 2 10 2H14C15.1046 2 16 2.89543 16 4V6H8V4Z" stroke="#F2374D"
              stroke-width="2" />
            <path d="M3 6H21" stroke="#F2374D" stroke-width="2" stroke-linecap="round" />
            <path
              d="M6.33 20.1867L5 6H19L17.67 20.1867C17.5736 21.2144 16.711 22 15.6787 22H8.32127C7.28902 22 6.42635 21.2144 6.33 20.1867Z"
              stroke="#F2374D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M10 18L9.5 10" stroke="#F2374D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
            <path d="M14 18L14.5 10" stroke="#F2374D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
          </svg>
        </button>
        <div class="relative inline-flex shrink-0 manage_menu_wrap">
          <button
            type="button"
            @click="show_manage = !show_manage"
            class="flex items-center last_in_row_button in_row_button square_button square_green_button"
          >
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <circle cx="12" cy="3.5" r="2.5" fill="#1FBC2F"/>
              <circle cx="12" cy="12" r="2.5" fill="#1FBC2F"/>
              <circle cx="12" cy="20.5" r="2.5" fill="#1FBC2F"/>
            </svg>
          </button>
          <div v-if="show_manage" class="manage_group">
            <button type="button" @click="openCsvUploadFromMenu" class="block manage_menu_item">
              Upload CSV
            </button>
            <a
              :href="locationsImportTemplatePath"
              :download="locationsImportTemplateFilename"
              title="Download location import template"
              class="block manage_menu_item manage_menu_link"
              @click="closeManageMenu"
            >
              Template
            </a>
          </div>
        </div>
      </div>
    </div>

    <!-- Context Menu -->
    <div v-if="contextMenu.show" :style="{
      position: 'fixed',
      left: contextMenu.x + 'px',
      top: contextMenu.y + 'px',
      zIndex: 1000
    }" class="context-menu">
      <div class="context-menu-item" @click.stop="handleAddNewLocation">
        <svg width="16" height="16" viewBox="0 0 19 18" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M9.5 1V16.9996" stroke="#1FBC2F" stroke-width="2" stroke-linecap="round" />
          <path d="M17.5 9L1.50038 9" stroke="#1FBC2F" stroke-width="2" stroke-linecap="round" />
        </svg>
        <span>Add New Location</span>
      </div>
      <div v-if="getContextLocation()" class="context-menu-item" @click.stop="handleAddEntry">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#66cb9e" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" > <path d="M7 22V2l10 5-10 5" /> </svg>
        <span>
          {{ hasEntryPoints(getContextLocation()) ? 'Move entry point here' : 'Add point as Entry' }}
        </span>
      </div>
      <div v-if="getContextLocation()" class="context-menu-item" @click.stop="handleAddExit">
        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="yellow" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" > <path d="M7 22V2l10 5-10 5" /> </svg>
        <span>
          {{ hasExitPoints(getContextLocation()) ? 'Move exit point here' : 'Add point as Exit' }}
        </span>
      </div>
      <div
        v-if="getContextLocation() && hasEntryPoints(getContextLocation()) && (contextMenu.clickedOnMarker || contextMenu.isEntryExitMarker)"
        class="context-menu-item"
        @click.stop="handleRemoveEntry"
      >
        <span>Remove entry point</span>
      </div>
      <div
        v-if="getContextLocation() && hasExitPoints(getContextLocation()) && (contextMenu.clickedOnMarker || contextMenu.isEntryExitMarker)"
        class="context-menu-item"
        @click.stop="handleRemoveExit"
      >
        <span>Remove exit point</span>
      </div>
    </div>

    <div class="main-content">
      <div class="locations-sidebar">
        <!-- Local Search Bar -->
        <div class="local-search-bar">
          <div class="search-container">
            <div class="search-input-wrapper">
              <input v-model="localSearchQuery" @input="handleLocalSearch" type="text"
                placeholder="Search locations by name or address..." class="search-input" />
              <svg class="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none"
                xmlns="http://www.w3.org/2000/svg">
                <path
                  d="M11 18C14.866 18 18 14.866 18 11C18 7.13401 14.866 4 11 4C7.13401 4 4 7.13401 4 11C4 14.866 7.13401 18 11 18Z"
                  stroke="#1FBC2F" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                <path d="M19 19L16 16" stroke="#1FBC2F" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </div>
            <button v-if="localSearchQuery" @click="clearLocalSearch" class="clear-search-btn">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M18 6L6 18M6 6L18 18" stroke="#6B7280" stroke-width="2" stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </button>
          </div>
          <div v-if="localSearchQuery && filteredLocations.length === 0" class="no-results">
            No locations found matching "{{ localSearchQuery }}"
          </div>
        </div>

        <div class="locations-list" v-if="locations">
          <div class="list-header py-3 px-4 flex text-left justify-between bg-gray-50 border-b">
            <div class="item flex items-center justify-center w-24px">
              <input v-model="checked_all" type='checkbox'>
            </div>
            <div class="item flex items-center justify-between flex-1 ml-4">
              <span class="strong_text">
                Location name
              </span>
              <span class="sort" @click="sortItems('name')" :class="{
                'rotate': ascending && sort_names.name,
                'active_arrow': sort_names.name
              }">
                <svg width="10" height="8" viewBox="0 0 10 8" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path
                    d="M5.82406 6.80139C5.42671 7.37935 4.57332 7.37936 4.17597 6.80139L0.576992 1.56653C0.120833 0.903028 0.595854 8.78263e-08 1.40103 1.58217e-07L8.59897 -1.66193e-07C9.40415 -9.58021e-08 9.87917 0.903026 9.42302 1.56653L5.82406 6.80139Z"
                    fill="black" />
                </svg>
              </span>
            </div>
            <div class="item delete_column w-44px">
            </div>
          </div>

          <div class="list-body">
            <div v-for="(item, index) in filteredLocations" :key="item.id" :ref="'location-item-' + index" :class="[
              'location-item py-4 px-4 flex text-left justify-between cursor-pointer border-b hover:bg-blue-50 transition-colors',
              checked_location[index] ? 'checked_row' : '',
              selectedLocationIndex === index ? 'bg-blue-100' : ''
            ]" @mouseenter="highlightLocation(index)" @mouseleave="clearHighlight"
              @click="selectLocation(item, index)">

              <div class="item flex items-center justify-center w-24px">
                <input :id="'checked-input-' + index" v-model="checked_location[index]" type='checkbox' @click.stop>
              </div>

              <div class="item flex items-center justify-start flex-1 ml-4">
                <div class="flex items-center">
                  <div
                    class="location-number mr-3 bg-blue-500 text-white rounded-full w-6 h-6 flex items-center justify-center text-sm font-bold">
                    {{ item._originalIndex + 1 }}
                  </div>
                  <div class="flex flex-col">
                    <div class="green_text">
                      {{ item.name }}
                    </div>
                    <div class="text-xs text-gray-500 mt-1">{{ item.address }}</div>
                    <div class="status-indicators text-xs mt-1">
                      <span v-if="item._saving" class="text-blue-500">saving…</span>
                      <span v-if="item._saved" class="text-green-500 font-bold">saved!</span>
                    </div>
                  </div>
                </div>
              </div>

              <div class="item flex items-center justify-center gap-2">
                <!-- Entry/Exit Point Indicators -->
                <div class="entry-exit-indicators flex items-center gap-2" v-if="hasEntryPoints(item) || hasExitPoints(item)">
                  <!-- Entry Point Indicator -->
                  <div v-if="hasEntryPoints(item)" class="point-indicator" :title="'Has ' + getEntryPointsCount(item) + ' entry point(s)'">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#66cb9e" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" > <path d="M7 22V2l10 5-10 5" /> </svg>
                  </div>
                  <!-- Exit Point Indicator -->
                  <div v-if="hasExitPoints(item)" class="point-indicator" :title="'Has ' + getExitPointsCount(item) + ' exit point(s)'">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="yellow" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" > <path d="M7 22V2l10 5-10 5" /> </svg>
                  </div>
                </div>
                <button @click.stop="deleteThisItem(item.id, index)"
                  class="flex items-center justify-center hover:bg-red-100 p-1 rounded">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M8 4C8 2.89543 8.89543 2 10 2H14C15.1046 2 16 2.89543 16 4V6H8V4Z" stroke="#F2374D"
                      stroke-width="2" />
                    <path d="M3 6H21" stroke="#F2374D" stroke-width="2" stroke-linecap="round" />
                    <path
                      d="M6.33 20.1867L5 6H19L17.67 20.1867C17.5736 21.2144 16.711 22 15.6787 22H8.32127C7.28902 22 6.42635 21.2144 6.33 20.1867Z"
                      stroke="#F2374D" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                    <path d="M10 18L9.5 10" stroke="#F2374D" stroke-width="2" stroke-linecap="round"
                      stroke-linejoin="round" />
                    <path d="M14 18L14.5 10" stroke="#F2374D" stroke-width="2" stroke-linecap="round"
                      stroke-linejoin="round" />
                  </svg>
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="map-container">
        <div id="locationsMapContainer" ref="mapContainer" class="w-full h-full">
        </div>
      </div>
    </div>

    <DeleteModal :entity="'location'" v-on:delete-select="deleteSelect" v-on:delete-close="closeDelete"
      ref="delete_modal" />

    <AddLocationModal ref="add_location_modal" @created="refreshAfterCreate" />

    <EntryExitPointModal ref="entry_exit_modal" @updated="refreshAfterPointUpdate" />

    <div class="blocked" v-if="show_manage" @click="closeManageMenu"></div>

    <CSVUploadLocations
      v-if="upload_csv"
      @cancel="onCsvUploadDismiss"
      @import-complete="onLocationsCsvImportComplete"
    />

  </div>
</template>

<script>
import { mapGetters, mapMutations } from "vuex";
import { infoMessage } from "../../services/messages";
import DeleteModal from "@/components/DeleteModal";
import AddLocationModal from "@/components/AddLocationModal.vue";
import EntryExitPointModal from "@/components/EntryExitPointModal.vue";
import CSVUploadLocations from "@/components/CSVUploadLocations";
import {
  LOCATIONS_IMPORT_TEMPLATE_FILENAME,
  LOCATIONS_IMPORT_TEMPLATE_PATH,
} from "@/constants/locationsImport";
import { getUserLocation } from "@/services/userLocation";

export default {
  name: "Locations",
  props: {
    rows_count: {
      type: Number,
      default: 10
    }
  },
  data() {
    return {
      locationsImportTemplatePath: LOCATIONS_IMPORT_TEMPLATE_PATH,
      locationsImportTemplateFilename: LOCATIONS_IMPORT_TEMPLATE_FILENAME,
      upload_csv: false,
      min_filter: true,
      sort_names: {
        name: true,
      },
      selected_name: { name: 'Location name', id: 'name' },
      user: null,
      checked_location: [],
      checked_all: false,
      show_buttons: false,
      show_manage: false,
      delete_ids: [],
      ascending: false,
      locations: [],
      locations_count: null,
      delete_id: null,
      selectedLocationIndex: null,
      localSearchQuery: '',
      map: null,
      platform: null,
      ui: null,
      markers: {},
      entryExitMarkers: [],
      selectedLocationId: null,
      tooltipBubble: null,
      geocoder: null,
      behavior: null,
      contextMenu: {
        show: false,
        x: 0,
        y: 0,
        lat: null,
        lng: null,
        address: null,
        clickedOnMarker: false,
        markerLocation: null,
        // context for entry/exit markers
        isEntryExitMarker: false,
        entryExitType: null,   // 'entry' | 'exit' | null
        entryExitIndex: null,  // number | null
      },
      contextMenuHandler: null,
      isRightClick: false,
      markerRightClicked: false,
      justOpenedContextMenu: false,
    }
  },
  methods: {
    ...mapMutations([]),
    async getUser() {
      this.user = JSON.parse(localStorage.getItem('user')) || null;
    },
    openAddLocation() {
      this.$refs.add_location_modal.open();
    },
    closeManageMenu() {
      this.show_manage = false;
    },
    openCsvUploadFromMenu() {
      this.closeManageMenu();
      this.openCsvUpload();
    },
    openCsvUpload() {
      this.upload_csv = true;
    },
    onCsvUploadDismiss() {
      this.upload_csv = false;
    },
    async onLocationsCsvImportComplete() {
      this.upload_csv = false;
      await this.getList();
      if (this.map) {
        this.syncMarkers();
        await this.getLocationsByRectangle();
      }
    },
    async refreshAfterCreate() {
      await this.getList();
    },
    async sortItems(name) {
      const j = this.sort_names;
      this.checked_trailer = [];
      this.checked_all = false;
      Object.keys(j).forEach(sort_name => {
        j[sort_name] = false;
      })
      this.ascending = !this.ascending;
      this.sort_names[name] = true
      await this.getList(name);
    },
    async getList(sortBy = 'name') {
      try {
        const requestData = {
          skip: 0,
          take: 10000, // Large number to get all locations
          sort: sortBy,
          column: this.getSelectedFilterItem || 'name',
          search: this.getSearchItemsNames || '',
          ascending: this.ascending,
        }

        const response = await this.$http
          .getAuth(`${this.$http.apiUrl()}/locations?skip=${requestData.skip}&take=${requestData.take}&sort-by=${requestData.sort}&column=${requestData.column}&search=${requestData.search}&sort=${requestData.ascending}`);

        this.locations = (response?.data?.locations?.data || []).map((loc, index) => {
          // Parse entry/exit if they are strings
          let entry = loc.entry;
          let exit = loc.exit;
          
          if (entry && typeof entry === 'string') {
            try {
              entry = entry.trim() === '' || entry === 'null' ? null : JSON.parse(entry);
            } catch (e) {
              console.error('Error parsing entry for location', loc.name, ':', e);
              entry = null;
            }
          }
          
          if (exit && typeof exit === 'string') {
            try {
              exit = exit.trim() === '' || exit === 'null' ? null : JSON.parse(exit);
            } catch (e) {
              console.error('Error parsing exit for location', loc.name, ':', e);
              exit = null;
            }
          }
          
          return {
            ...loc,
            entry: entry,
            exit: exit,
            _saving: false,
            _saved: false,
            _originalIndex: index
          };
        });

        this.locations_count = response?.data?.locations?.count || null;

        // Debug: Check entry/exit data
        console.log('=== LOCATIONS WITH ENTRY/EXIT ===');
        this.locations.forEach(loc => {
          if (loc.entry || loc.exit) {
            console.log(`Location: ${loc.name}`);
            console.log('  entry:', loc.entry, 'type:', typeof loc.entry);
            console.log('  exit:', loc.exit, 'type:', typeof loc.exit);
            console.log('  hasEntry:', this.hasEntryPoints(loc));
            console.log('  hasExit:', this.hasExitPoints(loc));
          }
        });

        if (this.locations.length > 0) {
          await this.$nextTick();
          if (!this.map) {
            await this.initMap();
          } else {
            // Clear existing markers and reload visible ones
            this.syncMarkers();
            this.getLocationsByRectangle();
          }
        }
      } catch (e) {
        console.log(e);
      }
    },

    async getLocationsByRectangle() {
      if (!this.map) return;

      try {
        const bounds = this.map.getViewModel().getLookAtData().bounds.getBoundingBox();
        const north = bounds.getTop();
        const south = bounds.getBottom();
        const east = bounds.getRight();
        const west = bounds.getLeft();

        const response = await this.$http.getAuth(
          `${this.$http.apiUrl()}/locations/by-rectangle?north=${north}&south=${south}&east=${east}&west=${west}`
        );

        const visibleLocations = (response?.data?.locations || []).map(loc => {
          // Parse entry/exit if they are strings
          let entry = loc.entry;
          let exit = loc.exit;
          
          if (entry && typeof entry === 'string') {
            try {
              entry = entry.trim() === '' || entry === 'null' ? null : JSON.parse(entry);
            } catch (e) {
              console.error('Error parsing entry for location', loc.name, ':', e);
              entry = null;
            }
          }
          
          if (exit && typeof exit === 'string') {
            try {
              exit = exit.trim() === '' || exit === 'null' ? null : JSON.parse(exit);
            } catch (e) {
              console.error('Error parsing exit for location', loc.name, ':', e);
              exit = null;
            }
          }
          
          return {
            ...loc,
            entry: entry,
            exit: exit,
            _saving: false,
            _saved: false
          };
        });

        // Update markers to show only visible locations
        this.syncMarkers();
        visibleLocations.forEach((loc) => {
          const originalLocation = this.locations.find(l => l.id === loc.id);
          if (originalLocation) {
            this.loadSingleMarker(originalLocation, originalLocation._originalIndex);
          }
        });
        
        // Re-display entry/exit points if a location is still selected
        if (this.selectedLocationId) {
          const selectedLocation = this.locations.find(l => l.id === this.selectedLocationId);
          if (selectedLocation) {
            this.displayEntryExitPoints(selectedLocation);
          }
        }
      } catch (e) {
        console.error('Error fetching locations by rectangle:', e);
      }
    },
    openDeleteModal() {
      this.$refs.delete_modal.show = true;
    },
    async dropChecked() {
      this.checked_location = [];
    },
    async deleteThisItem(id, index) {
      this.delete_id = id;
      await this.dropChecked();
      this.$refs.delete_modal.show = true;
      if (!this.checked_location[index]) {
        document.getElementById('checked-input-' + index).click();
      }
    },
    closeDelete() {
      this.delete_id = null;
    },
    async deleteSelect() {
      let deleted;
      if (this.delete_id) {
        deleted = await this.$http.deleteAuth(`${this.$http.apiUrl()}/locations/${this.delete_id}/delete`);
      } else {
        deleted = await this.$http.postAuth(`${this.$http.apiUrl()}/locations/multiple-deletion`, { ids: this.delete_ids });
      }

      this.delete_ids = [];
      this.checked_trailer = [];
      if (deleted?.data?.err) {
        console.log(deleted?.data?.err);
        this.delete_id = null;
        return infoMessage(
          'Oops. Something went wrong...',
          this.getTopBanner ? 'error open_top_banner' : 'error'
        );
      }

      infoMessage(
        this.delete_id ? `Location deleted` : `locations deleted`,
        this.getTopBanner ? 'success open_top_banner' : 'success'
      );
      this.delete_id = null;
      await this.dropChecked();

      await this.getList();
    },
    handleLocalSearch() {
      this.selectedLocationIndex = null;
    },

    clearLocalSearch() {
      this.localSearchQuery = '';
      this.selectedLocationIndex = null;
    },

    async initMap() {
      if (!window.H) {
        console.error('HERE Maps API not loaded');
        return;
      }

      this.platform = new H.service.Platform({
        apikey: process.env.VUE_APP_HERE_API_KEY,
      });
      const engineType = H.Map.EngineType.HARP;
      this.geocoder = this.platform.getSearchService();

      const defaultLayers = this.platform.createDefaultLayers({ engineType });

      const logisticsLayer =
        defaultLayers.vector.normal.logistics

      const firstLocation = this.locations[0];

      let center = (firstLocation && firstLocation.geo)
        ? { lat: firstLocation.geo.latitude, lng: firstLocation.geo.longitude }
        : null;

      if (!center) {
        const userLocation = await getUserLocation();
        center = { lat: userLocation.lat, lng: userLocation.lng };
      }

      this.map = new H.Map(
        this.$refs.mapContainer,
        logisticsLayer,
        {
          zoom: 16,
          center: center,
          engineType
        }
      );

      this.behavior = new H.mapevents.Behavior(new H.mapevents.MapEvents(this.map));
      this.ui = H.ui.UI.createDefault(this.map, defaultLayers);
      this.map.addLayer(defaultLayers.vector.traffic.logistics);
      const style = this.map.getBaseLayer().getProvider().getStyle();

      const styleChangeHandler = () => {
        if (style.getState() === H.map.render.Style.State.READY) {
          style.removeEventListener('change', styleChangeHandler);

          const enabledFeatures = [
            { feature: 'vehicle restrictions', mode: 'active & inactive' },
            { feature: 'congestion zones', mode: 'all' },
            { feature: 'environmental zones', mode: 'all' }
          ];
          style.setEnabledFeatures(enabledFeatures);
        }
      };
      style.addEventListener('change', styleChangeHandler);

      window.addEventListener('resize', () => this.map.getViewPort().resize());
      const mapElement = this.$refs.mapContainer;
      if (mapElement) {
        this.contextMenuHandler = (evt) => {
          evt.preventDefault();
          evt.stopPropagation();
          this.justOpenedContextMenu = true;
          const mapRect = this.$refs.mapContainer.getBoundingClientRect();
          const x = evt.clientX - mapRect.left;
          const y = evt.clientY - mapRect.top;
          const objectsAtPoint = this.map.getObjectsAt(x, y);
          
          if (objectsAtPoint && objectsAtPoint.length > 0) {
            // Check if right-clicked on entry/exit marker
            const entryExitMarker = objectsAtPoint.find(obj => {
              const data = obj.getData && obj.getData();
              return data && (data.type === 'entry' || data.type === 'exit');
            });
            
            if (entryExitMarker) {
              const data = entryExitMarker.getData && entryExitMarker.getData();
              const geo = entryExitMarker.getGeometry && entryExitMarker.getGeometry();
              if (data && geo) {
                this.contextMenu.lat = geo.lat;
                this.contextMenu.lng = geo.lng;
                this.contextMenu.x = evt.clientX;
                this.contextMenu.y = evt.clientY;
                this.contextMenu.clickedOnMarker = false;
                this.contextMenu.isEntryExitMarker = true;
                this.contextMenu.entryExitType = data.type;        // 'entry' or 'exit'
                this.contextMenu.entryExitIndex = data.pointIndex; // number
                this.contextMenu.markerLocation = data.location;
                if (data.location && data.location.id) {
                  this.selectedLocationId = data.location.id;
                }
                this.contextMenu.address = data.address || '';
                this.contextMenu.show = true;
              }
              setTimeout(() => {
                this.justOpenedContextMenu = false;
              }, 100);
              return;
            }
            
            // Check if right-clicked on location marker
            const clickedMarker = objectsAtPoint.find(obj => {
              const data = obj.getData && obj.getData();
              return data && data.id && !data.type; // Location marker has id but no type
            });
            
            if (clickedMarker) {
              const markerData = clickedMarker.getData();
              if (markerData) {
                const fakeEvt = {
                  currentPointer: {
                    viewportX: x,
                    viewportY: y
                  }
                };
                this.handleMarkerRightClick(fakeEvt, markerData);
                setTimeout(() => {
                  this.justOpenedContextMenu = false;
                }, 100);
                return;
              }
            }
          }
          this.handleMapRightClick(evt);
          setTimeout(() => {
            this.justOpenedContextMenu = false;
          }, 100);
        };
        mapElement.addEventListener('contextmenu', this.contextMenuHandler);
      }
      this.map.addEventListener('tap', (evt) => {
        // Ignore right-click taps so we don't clear flags on context menu
        if (evt.originalEvent && evt.originalEvent.button === 2) {
          return;
        }

        // Check if click was on an entry/exit marker
        const objectsAtPoint = this.map.getObjectsAt(evt.currentPointer.viewportX, evt.currentPointer.viewportY);
        const clickedOnEntryExit = objectsAtPoint && objectsAtPoint.some(obj => {
          const data = obj.getData && obj.getData();
          return data && (data.type === 'entry' || data.type === 'exit');
        });
        
        // Don't clear entry/exit markers if clicking on them
        if (!clickedOnEntryExit) {
          this.hideContextMenu();
          // Only clear entry/exit markers when clicking on empty map space
          const clickedOnLocationMarker = objectsAtPoint && objectsAtPoint.some(obj => {
            const data = obj.getData && obj.getData();
            return data && data.id && !data.type; // Location marker has id but no type
          });
          
          if (!clickedOnLocationMarker) {
            // Clear entry/exit markers but keep the selected location from left panel
            this.clearEntryExitMarkers();
            // Don't clear selectedLocationId - user should be able to right-click and add entry/exit points
            // Only clear it when explicitly selecting a different location via selectLocation()
          }
        }
      });
      this.map.addEventListener('mapviewchangeend', () => {
        this.getLocationsByRectangle();
        // Re-display entry/exit points after map view changes if location is selected
        if (this.selectedLocationId && this.map) {
          const selectedLocation = this.locations.find(l => l.id === this.selectedLocationId);
          if (selectedLocation) {
            // Small delay to ensure markers are loaded first
            setTimeout(() => {
              this.displayEntryExitPoints(selectedLocation);
            }, 100);
          }
        }
      });

      // Initial load of locations in visible area
      await this.$nextTick();
      this.getLocationsByRectangle();
    },

    loadSingleMarker(loc, idx) {
      if (!this.map || !loc.geo || loc.geo.latitude === null || loc.geo.longitude === null) return;

      if (this.markers[loc.id]) {
        this.map.removeObject(this.markers[loc.id]);
      }

      // const svg = `<svg xmlns="http://www.w3.org/2000/svg" width="30" height="42" viewBox="0 0 30 42">
      //     <path fill="#1976d2" stroke="#fff" stroke-width="2" d="M15,40 C15,40 2,24 2,15 C2,8 8,2 15,2 C22,2 28,8 28,15 C28,24 15,40 15,40 Z" />
      //     <text x="15" y="20" text-anchor="middle" fill="#fff" font-size="12px" font-weight="bold">${loc._originalIndex + 1}</text>
      //   </svg>`;

      const svg = `<svg version="1.0" xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 64 64" xml:space="preserve"><path fill="#fff" d="m32 52.789-12-18C18.5 32 16 28.031 16 24c0-8.836 7.164-16 16-16s16 7.164 16 16c0 4.031-2.055 8-4 10.789z"/><path d="M32 0C18.746 0 8 10.746 8 24c0 5.219 1.711 10.008 4.555 13.93.051.094.059.199.117.289l16 24a4 4 0 0 0 6.656 0l16-24c.059-.09.066-.195.117-.289C54.289 34.008 56 29.219 56 24 56 10.746 45.254 0 32 0m12 34.789-12 18-12-18C18.5 32 16 28.031 16 24c0-8.836 7.164-16 16-16s16 7.164 16 16c0 4.031-2.055 8-4 10.789"/><text x="32" y="29" text-anchor="middle" font-family="Arial, Helvetica, sans-serif" font-size="16" font-weight="400">${loc._originalIndex + 1}</text></svg>`;

      const icon = new H.map.Icon('data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg), { anchor: { x: 15, y: 40 } });

      const marker = new H.map.Marker({ lat: loc.geo.latitude, lng: loc.geo.longitude }, { icon: icon });
      marker.draggable = true;
      marker.setData(loc);

      // Track right-click on marker
      marker.addEventListener('pointerdown', (evt) => {
        if (evt.originalEvent && evt.originalEvent.button === 2) {
          this.isRightClick = true;
          this.markerRightClicked = true;
          evt.stopPropagation();
        }
      });

      marker.addEventListener('tap', (evt) => {
        evt.stopPropagation();
        if (this.isRightClick) {
          this.isRightClick = false;
          this.handleMarkerRightClick(evt, loc);
        } else {
          // Left click - select location and show entry/exit points
          this.selectLocation(loc, idx);
        }
      });

      marker.addEventListener('pointerenter', () => { });
      marker.addEventListener('pointerleave', () => { });

      marker.addEventListener('dragstart', () => {
        if (this.behavior) this.behavior.disable(H.mapevents.Behavior.DRAGGING);
      }, false);

      marker.addEventListener('drag', (ev) => {
        const pointer = ev.currentPointer;
        const newPosition = this.map.screenToGeo(pointer.viewportX, pointer.viewportY);
        ev.target.setGeometry(newPosition);
      }, false);

      marker.addEventListener('dragend', (e) => {
        if (this.behavior) this.behavior.enable(H.mapevents.Behavior.DRAGGING);
        this.handleDragEnd(loc, e);
      }, false);

      this.map.addObject(marker);
      this.markers[loc.id] = marker;
    },

    syncMarkers() {
      // This method is now used only for clearing all markers when needed
      if (!this.map) return;

      const existingMarkers = Object.values(this.markers);
      if (existingMarkers.length > 0) this.map.removeObjects(existingMarkers);
      this.markers = {};
      // Don't clear entry/exit markers here - they should persist
    },

    clearEntryExitMarkers() {
      if (!this.map) return;
      
      if (this.entryExitMarkers && this.entryExitMarkers.length > 0) {
        this.map.removeObjects(this.entryExitMarkers);
      }
      this.entryExitMarkers = [];
    },

    displayEntryExitPoints(location) {
      if (!this.map || !location) return;
      
      // Clear any existing entry/exit markers first (but preserve if same location)
      if (this.selectedLocationId !== location.id) {
        this.clearEntryExitMarkers();
      } else {
        // If same location, just clear and re-add to avoid duplicates
        this.clearEntryExitMarkers();
      }
      
      // Display entry points (green)
      if (location.entry) {
        let entryData = location.entry;
        
        // Parse if string
        if (typeof entryData === 'string') {
          try {
            entryData = entryData.trim() === '' || entryData === 'null' ? null : JSON.parse(entryData);
          } catch (e) {
            console.error('Error parsing entry data:', e);
            entryData = null;
          }
        }
        
        if (entryData) {
          const entryArray = Array.isArray(entryData) ? entryData : [entryData];
          entryArray.forEach((entryPoint, idx) => {
            if (entryPoint && entryPoint.coordinates) {
              const coords = entryPoint.coordinates;
              if (coords.latitude && coords.longitude) {
                this.addEntryExitMarker(
                  coords.latitude,
                  coords.longitude,
                  'entry',
                  entryPoint.address || `Entry Point ${idx + 1}`,
                  idx,
                  location
                );
              }
            }
          });
        }
      }
      
      // Display exit points (red)
      if (location.exit) {
        let exitData = location.exit;
        
        // Parse if string
        if (typeof exitData === 'string') {
          try {
            exitData = exitData.trim() === '' || exitData === 'null' ? null : JSON.parse(exitData);
          } catch (e) {
            console.error('Error parsing exit data:', e);
            exitData = null;
          }
        }
        
        if (exitData) {
          const exitArray = Array.isArray(exitData) ? exitData : [exitData];
          exitArray.forEach((exitPoint, idx) => {
            if (exitPoint && exitPoint.coordinates) {
              const coords = exitPoint.coordinates;
              if (coords.latitude && coords.longitude) {
                this.addEntryExitMarker(
                  coords.latitude,
                  coords.longitude,
                  'exit',
                  exitPoint.address || `Exit Point ${idx + 1}`,
                  idx,
                  location
                );
              }
            }
          });
        }
      }
    },

    addEntryExitMarker(lat, lng, type, address, pointIndex, location) {
      if (!this.map || !location) return;
      
      // Use provided SVG icons for entry/exit points - smaller than location marker (30x42)
      const entrySvg = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="#66cb9e" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" > <path d="M7 22V2l10 5-10 5" /> </svg>`;
      const exitSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" fill="yellow" stroke="#000000" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" > <path d="M7 22V2l10 5-10 5" /> </svg>`;
      
      const svg = type === 'entry' ? entrySvg : exitSvg;
      
      const icon = new H.map.Icon(
        'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(svg),
        { anchor: { x: 12, y: 24 } } // Anchor at center bottom (24x24 icon, center at 12, bottom at 24)
      );
      
      const marker = new H.map.Marker(
        { lat: lat, lng: lng },
        { icon: icon }
      );
      
      // Make marker draggable
      marker.draggable = true;
      
      // Store original data for reference
      const originalLat = lat;
      const originalLng = lng;
      
      // Add tooltip with address
      marker.setData({
        type: type,
        address: address,
        coordinates: { latitude: lat, longitude: lng },
        pointIndex: pointIndex,
        location: location
      });
      
      // Prevent right-click from clearing markers
      marker.addEventListener('pointerdown', (evt) => {
        if (evt.originalEvent && evt.originalEvent.button === 2) {
          evt.stopPropagation();
        }
      });
      
      // Drag event handlers
      marker.addEventListener('dragstart', () => {
        if (this.behavior) this.behavior.disable(H.mapevents.Behavior.DRAGGING);
      }, false);

      marker.addEventListener('drag', (ev) => {
        const pointer = ev.currentPointer;
        const newPosition = this.map.screenToGeo(pointer.viewportX, pointer.viewportY);
        ev.target.setGeometry(newPosition);
      }, false);

      marker.addEventListener('dragend', () => {
        if (this.behavior) this.behavior.enable(H.mapevents.Behavior.DRAGGING);
        this.handleEntryExitDragEnd(marker, type, pointIndex, location, originalLat, originalLng);
      }, false);
      
      this.map.addObject(marker);
      this.entryExitMarkers.push(marker);
    },

    async handleEntryExitDragEnd(marker, type, pointIndex, location, oldLat, oldLng) {
      const pos = marker.getGeometry();
      const newLat = pos.lat;
      const newLng = pos.lng;
      
      // Update marker data
      const markerData = marker.getData();
      markerData.coordinates = { latitude: newLat, longitude: newLng };
      
      // Find the location in the locations array
      const locationIndex = this.locations.findIndex(l => l.id === location.id);
      if (locationIndex === -1) return;
      
      const loc = this.locations[locationIndex];
      
      // Get the entry/exit data
      let pointsData = type === 'entry' ? loc.entry : loc.exit;
      
      // Parse if string
      if (typeof pointsData === 'string') {
        try {
          pointsData = pointsData.trim() === '' || pointsData === 'null' ? null : JSON.parse(pointsData);
        } catch (e) {
          console.error('Error parsing points data:', e);
          pointsData = null;
        }
      }
      
      if (!pointsData) {
        // Revert position if no data
        marker.setGeometry({ lat: oldLat, lng: oldLng });
        alert(`Failed to update ${type} point: No ${type} data found.`);
        return;
      }
      
      // Convert to array if single object
      const pointsArray = Array.isArray(pointsData) ? pointsData : [pointsData];
      
      if (pointIndex >= pointsArray.length) {
        marker.setGeometry({ lat: oldLat, lng: oldLng });
        alert(`Failed to update ${type} point: Invalid index.`);
        return;
      }
      
      // Update the point coordinates
      const pointToUpdate = pointsArray[pointIndex];
      pointToUpdate.coordinates = {
        latitude: newLat,
        longitude: newLng
      };
      
      // Get new address via reverse geocoding
      this.geocoder.reverseGeocode(
        { at: `${newLat},${newLng}` },
        async (result) => {
          let newAddress = pointToUpdate.address || `${type === 'entry' ? 'Entry' : 'Exit'} Point ${pointIndex + 1}`;
          
          if (result.items.length > 0) {
            newAddress = result.items[0].address.label;
          }
          
          // Update the address in the point data
          pointToUpdate.address = newAddress;
          markerData.address = newAddress;
          
          // Update the location's entry/exit data
          const updatedPoints = Array.isArray(pointsData) ? pointsArray : pointsArray[0];
          
          if (type === 'entry') {
            loc.entry = updatedPoints;
          } else {
            loc.exit = updatedPoints;
          }
          
          // Save to API
          try {
            const updateData = {
              [type]: updatedPoints
            };
            
            const response = await this.$http.putAuth(
              `${this.$http.apiUrl()}/locations/${location.id}/update`,
              updateData
            );
            
            if (response?.data?.statusCode === 200) {
              console.log(`${type} point updated successfully:`, response.data);
              // Popup removed as requested
            } else {
              throw new Error(`Update failed with status: ${response?.data?.statusCode || 'unknown'}`);
            }
          } catch (apiError) {
            console.error(`API Update Error for ${type} point:`, apiError);
            // Revert position on error
            marker.setGeometry({ lat: oldLat, lng: oldLng });
            pointToUpdate.coordinates = {
              latitude: oldLat,
              longitude: oldLng
            };
            
            const errorMessage = apiError.response?.data?.message || apiError.message || 'Unknown error';
            alert(`Failed to update ${type} point: ${errorMessage}`);
          }
        },
        (geocodeError) => {
          console.error('Reverse Geocoding Error:', geocodeError);
          // Still try to save coordinates without address update
          const updatedPoints = Array.isArray(pointsData) ? pointsArray : pointsArray[0];
          
          if (type === 'entry') {
            loc.entry = updatedPoints;
          } else {
            loc.exit = updatedPoints;
          }
          
          // Save to API without address update
          this.saveEntryExitPoint(location.id, type, updatedPoints, marker, oldLat, oldLng);
        }
      );
    },

    async saveEntryExitPoint(locationId, type, pointsData, marker, oldLat, oldLng) {
      try {
        const updateData = {
          [type]: pointsData
        };
        
        const response = await this.$http.putAuth(
          `${this.$http.apiUrl()}/locations/${locationId}/update`,
          updateData
        );
        
        if (response?.data?.statusCode === 200) {
          console.log(`${type} point coordinates updated successfully:`, response.data);
        } else {
          throw new Error(`Update failed with status: ${response?.data?.statusCode || 'unknown'}`);
        }
      } catch (apiError) {
        console.error(`Coordinate API Update Error for ${type} point:`, apiError);
        // Revert position on error
        marker.setGeometry({ lat: oldLat, lng: oldLng });
        
        const errorMessage = apiError.response?.data?.message || apiError.message || 'Unknown error';
        alert(`Failed to update ${type} point coordinates: ${errorMessage}`);
      }
    },

    async handleRemoveEntry() {
      const location = this.getContextLocation();
      if (!location) {
        this.hideContextMenu();
        return;
      }

      const locationIndex = this.locations.findIndex(l => l.id === location.id);
      if (locationIndex === -1) {
        this.hideContextMenu();
        return;
      }
      const loc = this.locations[locationIndex];

      let entryData = loc.entry;
      if (!entryData) {
        this.hideContextMenu();
        return;
      }

      if (typeof entryData === 'string') {
        try {
          entryData = entryData.trim() === '' || entryData === 'null' ? null : JSON.parse(entryData);
        } catch (e) {
          console.error('Error parsing entry data for remove:', e);
          this.hideContextMenu();
          return;
        }
      }

      let entryArray = Array.isArray(entryData) ? entryData : [entryData];

      if (this.contextMenu.isEntryExitMarker && this.contextMenu.entryExitType === 'entry' && typeof this.contextMenu.entryExitIndex === 'number') {
        // Remove only this point
        entryArray = entryArray.filter((_, idx) => idx !== this.contextMenu.entryExitIndex);
      } else {
        // Remove all entry points for this location
        entryArray = [];
      }

      const updatedEntry = entryArray.length === 0 ? null : (entryArray.length === 1 ? entryArray[0] : entryArray);
      loc.entry = updatedEntry;

      this.hideContextMenu();

      try {
        const updateData = { entry: updatedEntry };
        const response = await this.$http.putAuth(
          `${this.$http.apiUrl()}/locations/${location.id}/update`,
          updateData
        );
        if (response?.data?.statusCode === 200) {
          console.log('Entry point(s) removed successfully');
          this.displayEntryExitPoints(loc);
        } else {
          throw new Error(`Update failed with status: ${response?.data?.statusCode || 'unknown'}`);
        }
      } catch (apiError) {
        console.error('API Update Error for remove entry:', apiError);
        alert('Failed to remove entry point');
      }
    },

    async handleRemoveExit() {
      const location = this.getContextLocation();
      if (!location) {
        this.hideContextMenu();
        return;
      }

      const locationIndex = this.locations.findIndex(l => l.id === location.id);
      if (locationIndex === -1) {
        this.hideContextMenu();
        return;
      }
      const loc = this.locations[locationIndex];

      let exitData = loc.exit;
      if (!exitData) {
        this.hideContextMenu();
        return;
      }

      if (typeof exitData === 'string') {
        try {
          exitData = exitData.trim() === '' || exitData === 'null' ? null : JSON.parse(exitData);
        } catch (e) {
          console.error('Error parsing exit data for remove:', e);
          this.hideContextMenu();
          return;
        }
      }

      let exitArray = Array.isArray(exitData) ? exitData : [exitData];

      if (this.contextMenu.isEntryExitMarker && this.contextMenu.entryExitType === 'exit' && typeof this.contextMenu.entryExitIndex === 'number') {
        // Remove only this point
        exitArray = exitArray.filter((_, idx) => idx !== this.contextMenu.entryExitIndex);
      } else {
        // Remove all exit points for this location
        exitArray = [];
      }

      const updatedExit = exitArray.length === 0 ? null : (exitArray.length === 1 ? exitArray[0] : exitArray);
      loc.exit = updatedExit;

      this.hideContextMenu();

      try {
        const updateData = { exit: updatedExit };
        const response = await this.$http.putAuth(
          `${this.$http.apiUrl()}/locations/${location.id}/update`,
          updateData
        );
        if (response?.data?.statusCode === 200) {
          console.log('Exit point(s) removed successfully');
          this.displayEntryExitPoints(loc);
        } else {
          throw new Error(`Update failed with status: ${response?.data?.statusCode || 'unknown'}`);
        }
      } catch (apiError) {
        console.error('API Update Error for remove exit:', apiError);
        alert('Failed to remove exit point');
      }
    },

    // These methods are now empty as per your request
    showTooltip() { },
    hideTooltip() { },

    async handleDragEnd(loc, e) {
      const pos = e.target.getGeometry();
      const oldLatitude = loc.geo.latitude;
      const oldLongitude = loc.geo.longitude;
      const oldAddress = loc.address;

      loc.geo.latitude = pos.lat;
      loc.geo.longitude = pos.lng;
      loc._saving = true;
      loc._saved = false;

      try {
        this.geocoder.reverseGeocode(
          { at: `${pos.lat},${pos.lng}` },
          async (result) => {
            let newAddress = 'Address not found at this location.';

            if (result.items.length > 0) {
              newAddress = result.items[0].address.label;
            }

            // Update the address in the reactive locations array
            const locationIndex = this.locations.findIndex(l => l.id === loc.id);
            if (locationIndex !== -1) {
              this.locations[locationIndex].address = newAddress;
              this.locations[locationIndex].geo.latitude = pos.lat;
              this.locations[locationIndex].geo.longitude = pos.lng;
            }

            try {
              const updateData = {
                geo: {
                  latitude: pos.lat,
                  longitude: pos.lng
                },
                address: newAddress
              };

              const response = await this.$http.putAuth(
                `${this.$http.apiUrl()}/locations/${loc.id}/update`,
                updateData
              );

              if (response?.data?.statusCode === 200) {
                if (locationIndex !== -1) {
                  this.locations[locationIndex]._saving = false;
                  this.locations[locationIndex]._saved = true;
                  setTimeout(() => {
                    if (this.locations[locationIndex]) {
                      this.locations[locationIndex]._saved = false;
                    }
                  }, 2000);
                }
                console.log('Location updated successfully:', response.data);
              } else {
                throw new Error(`Update failed with status: ${response?.data?.statusCode || 'unknown'}`);
              }
            } catch (apiError) {
              console.error('API Update Error:', apiError);
              if (locationIndex !== -1) {
                this.locations[locationIndex]._saving = false;
                this.locations[locationIndex].geo.latitude = oldLatitude;
                this.locations[locationIndex].geo.longitude = oldLongitude;
                this.locations[locationIndex].address = oldAddress;
              }
              e.target.setGeometry({ lat: oldLatitude, lng: oldLongitude });

              const errorMessage = apiError.response?.data?.message || apiError.message || 'Unknown error';
              alert(`Failed to update location: ${errorMessage}`);
            }
          },
          (geocodeError) => {
            console.error('Reverse Geocoding Error:', geocodeError);
            const locationIndex = this.locations.findIndex(l => l.id === loc.id);
            if (locationIndex !== -1) {
              this.locations[locationIndex]._saving = false;
            }
            this.updateLocationCoordinates(loc, pos, oldLatitude, oldLongitude, e, oldAddress);
          }
        );
      } catch (error) {
        console.error('Drag End Error:', error);
        const locationIndex = this.locations.findIndex(l => l.id === loc.id);
        if (locationIndex !== -1) {
          this.locations[locationIndex]._saving = false;
          this.locations[locationIndex].geo.latitude = oldLatitude;
          this.locations[locationIndex].geo.longitude = oldLongitude;
          this.locations[locationIndex].address = oldAddress;
        }
        e.target.setGeometry({ lat: oldLatitude, lng: oldLongitude });
        alert('An error occurred while updating the location.');
      }
    },

    async updateLocationCoordinates(loc, pos, oldLatitude, oldLongitude, e, oldAddress) {
      try {
        const updateData = {
          geo: {
            latitude: pos.lat,
            longitude: pos.lng
          }
        };

        const response = await this.$http.putAuth(
          `${this.$http.apiUrl()}/locations/${loc.id}/update`,
          updateData
        );

        if (response?.data?.statusCode === 200) {
          const locationIndex = this.locations.findIndex(l => l.id === loc.id);
          if (locationIndex !== -1) {
            this.locations[locationIndex]._saving = false;
            this.locations[locationIndex]._saved = true;
            this.locations[locationIndex].geo.latitude = pos.lat;
            this.locations[locationIndex].geo.longitude = pos.lng;
            setTimeout(() => {
              if (this.locations[locationIndex]) {
                this.locations[locationIndex]._saved = false;
              }
            }, 2000);
          }
          console.log('Location coordinates updated successfully:', response.data);
        } else {
          throw new Error(`Coordinate update failed with status: ${response?.data?.statusCode || 'unknown'}`);
        }
      } catch (apiError) {
        console.error('Coordinate API Update Error:', apiError);
        const locationIndex = this.locations.findIndex(l => l.id === loc.id);
        if (locationIndex !== -1) {
          this.locations[locationIndex]._saving = false;
          this.locations[locationIndex].geo.latitude = oldLatitude;
          this.locations[locationIndex].geo.longitude = oldLongitude;
          if (oldAddress) {
            this.locations[locationIndex].address = oldAddress;
          }
        }
        e.target.setGeometry({ lat: oldLatitude, lng: oldLongitude });

        const errorMessage = apiError.response?.data?.message || apiError.message || 'Unknown error';
        alert(`Failed to update location coordinates: ${errorMessage}`);
      }
    },

    selectLocation(location, index) {
      this.selectedLocationIndex = index;
      this.selectedLocationId = location.id;
      
      if (!this.markers[location.id]) {
        this.loadSingleMarker(location, location._originalIndex);
      }

      const marker = this.markers[location.id];
      if (!marker || !this.map) return;
      
      // Clear previous entry/exit markers
      this.clearEntryExitMarkers();
      
      // Display entry/exit points for this location
      this.displayEntryExitPoints(location);
      
      this.map.getViewModel().setLookAtData({
        position: marker.getGeometry(),
        zoom: 16, // Approximately 200m view
      }, true);
      this.$nextTick(() => {
        const itemRef = this.$refs['location-item-' + index];
        if (itemRef && itemRef[0]) {
          itemRef[0].scrollIntoView({
            behavior: 'smooth',
            block: 'nearest'
          });
        }
      });
    },
    highlightLocation() { },
    clearHighlight() { },

    hasEntryPoints(location) {
      if (!location || !location.entry) return false;
      let entryData = location.entry;
      
      // Handle string JSON
      if (typeof entryData === 'string') {
        if (entryData.trim() === '' || entryData === 'null') return false;
        try {
          entryData = JSON.parse(entryData);
        } catch (e) {
          console.error('Error parsing entry data:', e, entryData);
          return false;
        }
      }
      
      // Check if it's an array with items OR a single object with coordinates
      if (Array.isArray(entryData) && entryData.length > 0) {
        return true;
      }
      
      if (typeof entryData === 'object' && entryData !== null && entryData.coordinates) {
        return true;
      }
      
      return false;
    },

    hasExitPoints(location) {
      if (!location || !location.exit) return false;
      let exitData = location.exit;
      
      // Handle string JSON
      if (typeof exitData === 'string') {
        if (exitData.trim() === '' || exitData === 'null') return false;
        try {
          exitData = JSON.parse(exitData);
        } catch (e) {
          console.error('Error parsing exit data:', e, exitData);
          return false;
        }
      }
      
      // Check if it's an array with items OR a single object with coordinates
      if (Array.isArray(exitData) && exitData.length > 0) {
        return true;
      }
      
      if (typeof exitData === 'object' && exitData !== null && exitData.coordinates) {
        return true;
      }
      
      return false;
    },

    getEntryPointsCount(location) {
      if (!location || !location.entry) return 0;
      
      let entryData = location.entry;
      
      // If it's a string, try to parse it
      if (typeof entryData === 'string') {
        if (entryData.trim() === '' || entryData === 'null' || entryData === 'undefined') return 0;
        try {
          entryData = JSON.parse(entryData);
        } catch (e) {
          console.error('Failed to parse entry data for', location.name, ':', entryData);
          return 0;
        }
      }
      
      // If it's an array, return length
      if (Array.isArray(entryData)) {
        return entryData.length;
      }
      
      // If it's an object (single entry), return 1
      if (typeof entryData === 'object' && entryData !== null) {
        return 1;
      }
      
      return 0;
    },

    getExitPointsCount(location) {
      if (!location || !location.exit) return 0;
      
      let exitData = location.exit;
      
      // If it's a string, try to parse it
      if (typeof exitData === 'string') {
        if (exitData.trim() === '' || exitData === 'null' || exitData === 'undefined') return 0;
        try {
          exitData = JSON.parse(exitData);
        } catch (e) {
          console.error('Failed to parse exit data for', location.name, ':', exitData);
          return 0;
        }
      }
      
      // If it's an array, return length
      if (Array.isArray(exitData)) {
        return exitData.length;
      }
      
      // If it's an object (single exit), return 1
      if (typeof exitData === 'object' && exitData !== null) {
        return 1;
      }
      
      return 0;
    },

    hideContextMenu(event) {
      if (this.justOpenedContextMenu) {
        return;
      }
      if (event && event.target) {
        const contextMenuEl = event.target.closest('.context-menu');
        if (contextMenuEl) {
          return;
        }
      }
      
      this.contextMenu.show = false;
      this.contextMenu.lat = null;
      this.contextMenu.lng = null;
      this.contextMenu.address = null;
      this.contextMenu.clickedOnMarker = false;
      this.contextMenu.markerLocation = null;
      this.contextMenu.isEntryExitMarker = false;
      this.contextMenu.entryExitType = null;
      this.contextMenu.entryExitIndex = null;
    },

    async handleMapRightClick(evt) {
      if (!this.map) return;
      
      const mapRect = this.$refs.mapContainer.getBoundingClientRect();
      const x = evt.clientX - mapRect.left;
      const y = evt.clientY - mapRect.top;
      const coord = this.map.screenToGeo(x, y);
      this.contextMenu.lat = coord.lat;
      this.contextMenu.lng = coord.lng;
      this.contextMenu.x = evt.clientX;
      this.contextMenu.y = evt.clientY;
      this.contextMenu.clickedOnMarker = false;
      this.contextMenu.isEntryExitMarker = false;
      this.contextMenu.entryExitType = null;
      this.contextMenu.entryExitIndex = null;
      
      // If a location is selected from the left panel, use it for entry/exit points
      if (this.selectedLocationId) {
        const selectedLocation = this.locations.find(l => l.id === this.selectedLocationId);
        if (selectedLocation) {
          this.contextMenu.markerLocation = selectedLocation;
        } else {
          this.contextMenu.markerLocation = null;
        }
      } else {
        this.contextMenu.markerLocation = null;
      }
      
      this.contextMenu.address = 'Loading address...';
      this.contextMenu.show = true;
      try {
        this.geocoder.reverseGeocode(
          { at: `${coord.lat},${coord.lng}` },
          (result) => {
            if (result.items.length > 0) {
              this.contextMenu.address = result.items[0].address.label;
            } else {
              this.contextMenu.address = `${coord.lat.toFixed(6)}, ${coord.lng.toFixed(6)}`;
            }
          },
          (error) => {
            console.error('Reverse geocoding error:', error);
            this.contextMenu.address = `${coord.lat.toFixed(6)}, ${coord.lng.toFixed(6)}`;
          }
        );
      } catch (error) {
        console.error('Error in reverse geocoding:', error);
        this.contextMenu.address = `${coord.lat.toFixed(6)}, ${coord.lng.toFixed(6)}`;
      }
    },

    async handleMarkerRightClick(evt, loc) {
      if (!this.map) return;
      
      const pointer = evt.currentPointer;
      const coord = { lat: loc.geo.latitude, lng: loc.geo.longitude };
      const mapRect = this.$refs.mapContainer.getBoundingClientRect();
      this.contextMenu.lat = coord.lat;
      this.contextMenu.lng = coord.lng;
      this.contextMenu.x = mapRect.left + pointer.viewportX;
      this.contextMenu.y = mapRect.top + pointer.viewportY;
      this.contextMenu.clickedOnMarker = true;
      this.contextMenu.markerLocation = loc;
      this.contextMenu.isEntryExitMarker = false;
      this.contextMenu.entryExitType = null;
      this.contextMenu.entryExitIndex = null;
      this.contextMenu.address = loc.address;
      this.contextMenu.show = true;
    },
    getContextLocation() {
      if (this.contextMenu.markerLocation) {
        return this.contextMenu.markerLocation;
      }
      if (this.selectedLocationId) {
        return this.locations.find(l => l.id === this.selectedLocationId) || null;
      }
      return null;
    },
    handleAddNewLocation() {
      console.log('=== handleAddNewLocation called ===');
      console.log('contextMenu:', JSON.stringify(this.contextMenu, null, 2));
      console.log('Modal ref exists:', !!this.$refs.add_location_modal);
      
      if (this.contextMenu.lat && this.contextMenu.lng && this.contextMenu.address) {
        const locationData = {
          latitude: this.contextMenu.lat,
          longitude: this.contextMenu.lng,
          address: this.contextMenu.address
        };
        
        console.log('Location data to pass:', locationData);
        
        // Hide context menu
        this.hideContextMenu();
        
        // Open modal in next tick
        this.$nextTick(() => {
          console.log('Opening modal...');
          if (this.$refs.add_location_modal) {
            this.$refs.add_location_modal.open(locationData);
            console.log('Modal open called');
          } else {
            console.error('Modal ref not found!');
          }
        });
      } else {
        console.error('Missing data - lat:', this.contextMenu.lat, 'lng:', this.contextMenu.lng, 'address:', this.contextMenu.address);
        this.hideContextMenu();
      }
    },

    async handleAddEntry() {
      // Get location from context menu (either from marker click or selected location)
      const location = this.contextMenu.markerLocation || (this.selectedLocationId ? this.locations.find(l => l.id === this.selectedLocationId) : null);
      
      if (!location) {
        this.hideContextMenu();
        return;
      }
      
      // Use right-click coordinates if available, otherwise calculate offset from location
      let newLat, newLng;
      if (this.contextMenu.lat && this.contextMenu.lng) {
        // Use the coordinates from right-click position
        newLat = this.contextMenu.lat;
        newLng = this.contextMenu.lng;
      } else {
        // Fallback: Calculate position slightly offset from location pin (about 10 meters northeast)
        const offsetLat = 0.0001; // approximately 10 meters
        const offsetLng = 0.0001;
        newLat = location.geo.latitude + offsetLat;
        newLng = location.geo.longitude + offsetLng;
      }
      
      // Hide context menu
      this.hideContextMenu();
      
      // Get address via reverse geocoding
      this.geocoder.reverseGeocode(
        { at: `${newLat},${newLng}` },
        async (result) => {
          let address = `Entry Point`;
          
          if (result.items.length > 0) {
            address = result.items[0].address.label;
          }
          
          // Find location in locations array
          const locationIndex = this.locations.findIndex(l => l.id === location.id);
          if (locationIndex === -1) return;
          
          const loc = this.locations[locationIndex];
          
          // Get existing entry data
          let entryData = loc.entry;
          
          // Parse if string
          if (typeof entryData === 'string') {
            try {
              entryData = entryData.trim() === '' || entryData === 'null' ? null : JSON.parse(entryData);
            } catch (e) {
              console.error('Error parsing entry data:', e);
              entryData = null;
            }
          }

          const hasExisting = entryData && (Array.isArray(entryData) ? entryData.length > 0 : true);

          // Normalize to array
          const entryArray = entryData
            ? (Array.isArray(entryData) ? entryData : [entryData])
            : [];

          if (hasExisting) {
            // MOVE existing entry point
            let targetIndex;
            if (this.contextMenu.isEntryExitMarker && this.contextMenu.entryExitType === 'entry' && typeof this.contextMenu.entryExitIndex === 'number') {
              targetIndex = this.contextMenu.entryExitIndex;
            } else {
              targetIndex = 0; // move first entry if multiple
            }
            if (targetIndex >= 0 && targetIndex < entryArray.length) {
              entryArray[targetIndex] = {
                ...(entryArray[targetIndex] || {}),
                coordinates: { latitude: newLat, longitude: newLng },
                address,
              };
            }
          } else {
            // ADD new entry point (current behavior)
            entryArray.push({
              coordinates: { latitude: newLat, longitude: newLng },
              address,
            });
          }

          const updatedEntry = entryArray.length === 1 ? entryArray[0] : entryArray;

          // Update location in array
          loc.entry = updatedEntry;
          
          // Save to API
          try {
            const updateData = { entry: updatedEntry };
            
            const response = await this.$http.putAuth(
              `${this.$http.apiUrl()}/locations/${location.id}/update`,
              updateData
            );
            
            if (response?.data?.statusCode === 200) {
              console.log(hasExisting ? 'Entry point moved successfully' : 'Entry point added successfully');
              // Re-display entry/exit points to show the updated marker
              this.displayEntryExitPoints(loc);
            } else {
              throw new Error(`Update failed with status: ${response?.data?.statusCode || 'unknown'}`);
            }
          } catch (apiError) {
            console.error('API Update Error:', apiError);
            const errorMessage = apiError.response?.data?.message || apiError.message || 'Unknown error';
            alert(`Failed to ${hasExisting ? 'move' : 'add'} entry point: ${errorMessage}`);
          }
        },
        async (geocodeError) => {
          console.error('Reverse Geocoding Error:', geocodeError);
          // Fallback: same as above but with default address
          const locationIndex = this.locations.findIndex(l => l.id === location.id);
          if (locationIndex === -1) return;
          
          const loc = this.locations[locationIndex];
          
          let entryData = loc.entry;
          if (typeof entryData === 'string') {
            try {
              entryData = entryData.trim() === '' || entryData === 'null' ? null : JSON.parse(entryData);
            } catch (e) {
              entryData = null;
            }
          }

          const hasExisting = entryData && (Array.isArray(entryData) ? entryData.length > 0 : true);
          const entryArray = entryData
            ? (Array.isArray(entryData) ? entryData : [entryData])
            : [];

          const fallbackAddress = 'Entry Point';

          if (hasExisting) {
            let targetIndex;
            if (this.contextMenu.isEntryExitMarker && this.contextMenu.entryExitType === 'entry' && typeof this.contextMenu.entryExitIndex === 'number') {
              targetIndex = this.contextMenu.entryExitIndex;
            } else {
              targetIndex = 0;
            }
            if (targetIndex >= 0 && targetIndex < entryArray.length) {
              entryArray[targetIndex] = {
                ...(entryArray[targetIndex] || {}),
                coordinates: { latitude: newLat, longitude: newLng },
                address: fallbackAddress,
              };
            }
          } else {
            entryArray.push({
              coordinates: { latitude: newLat, longitude: newLng },
              address: fallbackAddress,
            });
          }

          const updatedEntry = entryArray.length === 1 ? entryArray[0] : entryArray;
          loc.entry = updatedEntry;

          try {
            const updateData = { entry: updatedEntry };
            const response = await this.$http.putAuth(
              `${this.$http.apiUrl()}/locations/${location.id}/update`,
              updateData
            );
            if (response?.data?.statusCode === 200) {
              this.displayEntryExitPoints(loc);
            }
          } catch (apiError) {
            console.error('API Update Error:', apiError);
          }
        }
      );
    },

    async handleAddExit() {
      // Get location from context menu (either from marker click or selected location)
      const location = this.contextMenu.markerLocation || (this.selectedLocationId ? this.locations.find(l => l.id === this.selectedLocationId) : null);
      
      if (!location) {
        this.hideContextMenu();
        return;
      }
      
      // Use right-click coordinates if available, otherwise calculate offset from location
      let newLat, newLng;
      if (this.contextMenu.lat && this.contextMenu.lng) {
        // Use the coordinates from right-click position
        newLat = this.contextMenu.lat;
        newLng = this.contextMenu.lng;
      } else {
        // Fallback: Calculate position slightly offset from location pin (about 10 meters southeast)
        const offsetLat = -0.0001; // approximately 10 meters
        const offsetLng = 0.0001;
        newLat = location.geo.latitude + offsetLat;
        newLng = location.geo.longitude + offsetLng;
      }
      
      // Hide context menu
      this.hideContextMenu();
      
      // Get address via reverse geocoding
      this.geocoder.reverseGeocode(
        { at: `${newLat},${newLng}` },
        async (result) => {
          let address = `Exit Point`;
          
          if (result.items.length > 0) {
            address = result.items[0].address.label;
          }
          
          // Find location in locations array
          const locationIndex = this.locations.findIndex(l => l.id === location.id);
          if (locationIndex === -1) return;
          
          const loc = this.locations[locationIndex];
          
          // Get existing exit data
          let exitData = loc.exit;
          
          // Parse if string
          if (typeof exitData === 'string') {
            try {
              exitData = exitData.trim() === '' || exitData === 'null' ? null : JSON.parse(exitData);
            } catch (e) {
              console.error('Error parsing exit data:', e);
              exitData = null;
            }
          }

          const hasExisting = exitData && (Array.isArray(exitData) ? exitData.length > 0 : true);

          const exitArray = exitData
            ? (Array.isArray(exitData) ? exitData : [exitData])
            : [];

          if (hasExisting) {
            // MOVE existing exit point
            let targetIndex;
            if (this.contextMenu.isEntryExitMarker && this.contextMenu.entryExitType === 'exit' && typeof this.contextMenu.entryExitIndex === 'number') {
              targetIndex = this.contextMenu.entryExitIndex;
            } else {
              targetIndex = 0;
            }
            if (targetIndex >= 0 && targetIndex < exitArray.length) {
              exitArray[targetIndex] = {
                ...(exitArray[targetIndex] || {}),
                coordinates: { latitude: newLat, longitude: newLng },
                address,
              };
            }
          } else {
            // ADD new exit point
            exitArray.push({
              coordinates: { latitude: newLat, longitude: newLng },
              address,
            });
          }

          const updatedExit = exitArray.length === 1 ? exitArray[0] : exitArray;
          loc.exit = updatedExit;
          
          // Save to API
          try {
            const updateData = {
              exit: updatedExit
            };
            
            const response = await this.$http.putAuth(
              `${this.$http.apiUrl()}/locations/${location.id}/update`,
              updateData
            );
            
            if (response?.data?.statusCode === 200) {
              console.log(hasExisting ? 'Exit point moved successfully' : 'Exit point added successfully');
              // Re-display entry/exit points to show the new marker
              this.displayEntryExitPoints(loc);
            } else {
              throw new Error(`Update failed with status: ${response?.data?.statusCode || 'unknown'}`);
            }
          } catch (apiError) {
            console.error('API Update Error:', apiError);
            const errorMessage = apiError.response?.data?.message || apiError.message || 'Unknown error';
            alert(`Failed to ${hasExisting ? 'move' : 'add'} exit point: ${errorMessage}`);
          }
        },
        async (geocodeError) => {
          console.error('Reverse Geocoding Error:', geocodeError);
          // Still add/move the point with default address
          const locationIndex = this.locations.findIndex(l => l.id === location.id);
          if (locationIndex === -1) return;
          
          const loc = this.locations[locationIndex];
          
          let exitData = loc.exit;
          if (typeof exitData === 'string') {
            try {
              exitData = exitData.trim() === '' || exitData === 'null' ? null : JSON.parse(exitData);
            } catch (e) {
              exitData = null;
            }
          }

          const hasExisting = exitData && (Array.isArray(exitData) ? exitData.length > 0 : true);
          const exitArray = exitData
            ? (Array.isArray(exitData) ? exitData : [exitData])
            : [];

          const fallbackAddress = 'Exit Point';

          if (hasExisting) {
            let targetIndex;
            if (this.contextMenu.isEntryExitMarker && this.contextMenu.entryExitType === 'exit' && typeof this.contextMenu.entryExitIndex === 'number') {
              targetIndex = this.contextMenu.entryExitIndex;
            } else {
              targetIndex = 0;
            }
            if (targetIndex >= 0 && targetIndex < exitArray.length) {
              exitArray[targetIndex] = {
                ...(exitArray[targetIndex] || {}),
                coordinates: { latitude: newLat, longitude: newLng },
                address: fallbackAddress,
              };
            }
          } else {
            exitArray.push({
              coordinates: { latitude: newLat, longitude: newLng },
              address: fallbackAddress,
            });
          }

          const updatedExit = exitArray.length === 1 ? exitArray[0] : exitArray;
          loc.exit = updatedExit;
          
          // Save to API
          try {
            const updateData = { exit: updatedExit };
            const response = await this.$http.putAuth(
              `${this.$http.apiUrl()}/locations/${location.id}/update`,
              updateData
            );
            if (response?.data?.statusCode === 200) {
              this.displayEntryExitPoints(loc);
            }
          } catch (apiError) {
            console.error('API Update Error:', apiError);
          }
        }
      );
    },

    async refreshAfterPointUpdate(locationId) {
      console.log('Entry/Exit point updated for location:', locationId);
      // Reload the locations list (which will also reload markers)
      await this.getList();
    },

    async loadHereMapsAPI() {
      return new Promise((resolve, reject) => {
        if (window.H && window.H.ui) {
          return resolve();
        }
        const scripts = [
          'https://js.api.here.com/v3/3.1/mapsjs-core.js',
          'https://js.api.here.com/v3/3.1/mapsjs-service.js',
          'https://js.api.here.com/v3/3.1/mapsjs-ui.js',
          'https://js.api.here.com/v3/3.1/mapsjs-mapevents.js'
        ];
        let loadedCount = 0;
        scripts.forEach(src => {
          const script = document.createElement('script');
          script.src = src;
          script.defer = true;
          script.onload = () => {
            loadedCount++;
            if (loadedCount === scripts.length) {
              resolve();
            }
          };
          script.onerror = reject;
          document.head.appendChild(script);
        });
      });
    },
  },
  async mounted() {
    await this.loadHereMapsAPI();
    await this.getUser();
    await this.getList();
    
    // Add global click listener to hide context menu
    document.addEventListener('click', this.hideContextMenu);
    
    // Prevent mouseup after contextmenu from closing the menu
    document.addEventListener('mouseup', (evt) => {
      if (evt.button === 2 && this.justOpenedContextMenu) {
        evt.preventDefault();
        evt.stopPropagation();
      }
    });
  },
  beforeDestroy() {
    if (this.map) {
      this.map.dispose();
      window.removeEventListener('resize', () => this.map.getViewPort().resize());
    }
    // Remove context menu event listener
    const mapElement = this.$refs.mapContainer;
    if (mapElement && this.contextMenuHandler) {
      mapElement.removeEventListener('contextmenu', this.contextMenuHandler);
    }
    // Remove global click listener
    document.removeEventListener('click', this.hideContextMenu);
  },
  computed: {
    ...mapGetters([
      'getItemsToShow',
      'getSelectedFilterItem',
      'getRowsToShift',
      'getSearchItemsNames',
      'getTopBanner',
    ]),

    filteredLocations() {
      if (!this.localSearchQuery || this.localSearchQuery.trim() === '') {
        return this.locations;
      }

      const query = this.localSearchQuery.toLowerCase().trim();
      return this.locations.filter(location => {
        const name = location.name ? location.name.toLowerCase() : '';
        const address = location.address ? location.address.toLowerCase() : '';

        return name.includes(query) || address.includes(query);
      });
    }
  },
  components: {
    DeleteModal,
    AddLocationModal,
    EntryExitPointModal,
    CSVUploadLocations,
  },
  watch: {
    getItemsToShow() {
      this.getList()
    },
    getSelectedFilterItem() {
      this.getList()
    },
    getRowsToShift() {
      this.getList()
    },
    getSearchItemsNames() {
      this.getList()
    },
    checked_location(val) {
      if (val) {
        let true_or_false = false;
        for (let key in this.checked_location) {
          if (this.checked_location[key]) {
            true_or_false = true;
            if (!this.delete_ids.includes(this.locations[key].id)) {
              this.delete_ids.push(this.locations[key].id);
            }
          } else {
            if (this.delete_ids.includes(this.locations[key].id)) {
              for (let x in this.delete_ids) {
                if (this.delete_ids[x] === this.locations[key].id) {
                  this.delete_ids.splice(x, 1);
                  break;
                }
              }
            }
          }
        }
        this.show_buttons = true_or_false;
      }
    },
    checked_all(val) {
      if (val) {
        for (let key in this.locations) {
          this.checked_location[key] = true;
          this.delete_ids.push(this.locations[key].id);
        }
        this.show_buttons = true;
      } else {
        for (let key in this.locations) {
          this.checked_location[key] = false;
        }
        this.delete_ids = [];
        this.show_buttons = false;
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.locations-container {
  min-height: calc(100vh - (147px + 18px));
  display: flex;
  flex-direction: column;
}

.main-content {
  display: flex;
  flex: 1;
  min-height: 0;
}

.locations-sidebar {
  width: 40%;
  min-width: 400px;
  border-right: 1px solid #e5e7eb;
  display: flex;
  flex-direction: column;
}

.local-search-bar {
  padding: 16px;
  border-bottom: 1px solid #e5e7eb;
  background-color: #f9fafb;
}

.search-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.search-input-wrapper {
  position: relative;
  flex: 1;
}

.search-input {
  width: 100%;
  padding: 12px 16px 12px 44px;
  border: 2px solid #e5e7eb;
  border-radius: 8px;
  font-size: 14px;
  transition: border-color 0.2s ease;
  background-color: white;

  &:focus {
    outline: none;
    border-color: #1FBC2F;
    box-shadow: 0 0 0 3px rgba(31, 188, 47, 0.1);
  }

  &::placeholder {
    color: #9ca3af;
  }
}

.search-icon {
  position: absolute;
  left: 14px;
  top: 50%;
  transform: translateY(-50%);
  pointer-events: none;
}

.clear-search-btn {
  padding: 8px;
  border: none;
  background: none;
  cursor: pointer;
  border-radius: 4px;
  transition: background-color 0.2s ease;

  &:hover {
    background-color: #f3f4f6;
  }
}

.no-results {
  margin-top: 12px;
  padding: 12px;
  background-color: #fef3c7;
  border: 1px solid #f59e0b;
  border-radius: 6px;
  color: #92400e;
  font-size: 14px;
  text-align: center;
}

.locations-list {
  display: flex;
  flex-direction: column;
  height: 100%;
}

.list-header {
  flex-shrink: 0;
  border-bottom: 1px solid #e5e7eb;
}

.list-body {
  flex: 1;
  overflow-y: auto;
  max-height: calc(100vh - 300px);
}

.location-item {
  transition: all 0.2s ease;
  border-bottom: 1px solid #f3f4f6;

  &:hover {
    background-color: #f8fafc;
  }

  &.checked_row {
    background-color: #FFF1CE;
  }
}

.location-number {
  min-width: 24px;
  min-height: 24px;
  font-size: 12px;
  font-weight: bold;
}

.map-container {
  flex: 1;
  min-height: 500px;
  position: relative;
}

.item {
  font-size: 14px;
  padding-right: 25px;
}

.actions {

  &:hover,
  &:active,
  &:focus {
    .hidden {
      display: block;
    }

    span {
      &>* {
        cursor: pointer;
      }

      &:first-child {
        &>* {
          margin-left: auto;
        }
      }
    }
  }
}

.title_line {
  padding: 30px 0 22px;
  border-bottom: none;
}

.filters {
  width: 95%;
  margin: auto;
}

.button_text {
  font-style: normal;
  font-weight: 700;
  font-size: 14px;
  line-height: 14px;
}

.in_row_button {
  margin: 0 15px;
  border-radius: 8px;
}

.last_in_row_button {
  margin-left: 15px;
  border-radius: 8px;
}

.square_button {
  padding: 10px;
  width: 44px;
  height: 44px;
  box-sizing: border-box;
}

.square_green_button {
  color: #1FBC2F;
  border: 2px solid #1FBC2F;
}

.square_red_button {
  color: #F2374D;
  border: 2px solid #F2374D;

  svg {
    path {
      stroke: #F2374D;
    }
  }
}

.square_red_button:disabled {
  color: #3d4852;
  border: 2px solid #3d4852;

  svg {
    path {
      stroke: #3d4852;
    }
  }
}

.strong_text {
  font-weight: 500;
  font-size: 14px;
  line-height: 14px;
  color: #000000 !important;
}

.delete_column {
  width: 49px;
}

.item {
  span {
    margin-left: 5px;
    color: #969A9F;
  }
}

.green_text {
  font-weight: 700;
  font-size: 14px;
  line-height: 14px;
  color: #1FBC2F !important;
}

.text_black {
  font-weight: 700;
  font-size: 14px;
  line-height: 14px;
  color: #000000 !important;
}

.span_cut {
  text-overflow: ellipsis;
  overflow: hidden;
  white-space: nowrap;
  color: black !important;
}

.checked_row {
  background-color: #FFF1CE;
}

.button_group {
  position: relative;

  .manage_menu_wrap {
    .manage_group {
      z-index: 99;
      position: absolute;
      top: 48px;
      right: 0;
      border-radius: 8px;
      box-sizing: border-box;
      border: 0.5px solid #D8C3AF;
      box-shadow: 5px 5px rgba(216, 195, 175, 0.5);
      padding: 10px;
      background-color: #FFFFFF;
      white-space: nowrap;
    }

    .manage_menu_item {
      display: block;
      margin: 10px;
      padding: 0;
      background: none;
      border: none;
      font: inherit;
      text-align: left;
      cursor: pointer;
      color: #000000;
    }

    .manage_menu_link {
      text-decoration: none;
    }
  }
}

.locations-container {
  overflow-x: hidden;
}

.blocked {
  position: fixed;
  inset: 0;
  z-index: 98;
}

.active_arrow {
  svg {
    path {
      fill: #28a745;
    }
  }
}

.icon_div {
  width: 16px;

  svg {
    width: 100%;
  }
}

// NOTE: Custom marker styles are no longer needed
// as the SVG marker is self-contained.

// Entry/Exit Point Indicators
.entry-exit-indicators {
  display: flex;
  align-items: center;
  gap: 8px;
}

.point-indicator {
  display: flex;
  align-items: center;
  gap: 4px;
}

.point-count {
  font-size: 11px;
  color: #6B7280;
  font-weight: 500;
}

// Context Menu Styles
.context-menu {
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
  padding: 4px 0;
  min-width: 200px;
  z-index: 1000;
}

.context-menu-item {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  cursor: pointer;
  transition: background-color 0.2s ease;
  font-size: 14px;
  color: #374151;

  svg {
    flex-shrink: 0;
  }

  &:hover {
    background-color: #f3f4f6;
  }

  &:active {
    background-color: #e5e7eb;
  }

  span {
    user-select: none;
  }
}

// Responsive design
@media (max-width: 768px) {
  .main-content {
    flex-direction: column;
  }

  .locations-sidebar {
    width: 100%;
    min-width: unset;
    max-height: 50vh;
  }

  .map-container {
    min-height: 50vh;
  }
}
</style>